export const getTemplatedValues = (v) =>
  v?.match(/({{){1}(\s)?[a-zA-Z]*(\s)?(}}){1}/g);

export const trimQuotes = (s) => s.replace(/['"]/g, "");

const withOptions = (str) => {
  const selectRegex = /{select:(.)*}/gi;

  if (str.match(selectRegex)) return true;
  return false;
};

export const templateToFlow = (templated) => {
  const values = templated.map((t) => t.replace(/[{}]/g, ""));

  return `(args: { ${values
    .map((v) => v.concat(": string"))
    .join(", ")} }) => string`;
};

export const processJsonToFlowtype = (json) => {
  const res = json;
  Object.keys(res).forEach((k) => {
    if (typeof res[k] === "string") {
      const templates = getTemplatedValues(res[k]);

      if (templates && templates.length > 0) {
        res[k] = templateToFlow(templates);
      } else res[k] = "string";
    } else if (res[k] != null && typeof res[k] === "object") {
      res[k] = processJsonToFlowtype(res[k]);
    } else throw Error(`${k}, ${res[k]}, not a string or object`);
  });
  return res;
};

export const generateFunctionValue = (template, args) => {
  const base = templateToFlow(args);
  let string = template.replace(/"(,)?$/g, "");

  if (string.lastIndexOf(",") === string.length - 1)
    string = string.substr(0, string.length - 1);

  const func = `template("${string}", args)`;

  return `${base.substr(0, base.lastIndexOf("string"))}${func}`;
};

export const generateTemplatedFunction = (line, templatedValues) => {
  const keyValue = line.split('": "');
  const func = generateFunctionValue(keyValue[1], templatedValues);

  return `${trimQuotes(keyValue[0])}: ${func},`;
};

const generateEmptyFunction = (key) => `${trimQuotes(key)}: () => "",`;

const requiresComplexTyping = (ids, translations, index) => {
  const locales = ["en", "de"];
  const idLine = ids.split("\n")[index];
  const [, id] = idLine.split('": "');
  const object = translations.find((t) => id.includes(t.id));

  if (!object)
    throw Error("Something went wrong when checking for complex typing", id);

  const template = locales.find(
    (locale) => getTemplatedValues(object.locales[locale]) != null,
  );

  if (template != null) return getTemplatedValues(object.locales[template]);
  return null;
};

export const processJsonToLocale = (json, ids, translations) =>
  json
    .split("\n")
    .map((line, idx) => {
      if (line.length === 0) return line;

      const openingBracket = /(.)*{(\s)?/g;
      const closingBracket = /(\s)*}(,)?(\s)?/g;
      const templatedValues = getTemplatedValues(line);

      if (templatedValues && templatedValues.length > 0)
        return generateTemplatedFunction(line, templatedValues);

      if (
        !withOptions(line) &&
        (line.match(openingBracket) || line.match(closingBracket))
      )
        return trimQuotes(line);

      const keyValue = line.split('": "');
      if (keyValue.length > 2)
        throw Error(
          `Split with ": " did not work. length > 2 (=${keyValue.length})`,
        );

      const [key, rawValue] = keyValue;
      if (!rawValue || rawValue === '"' || rawValue === '",') {
        const withComplexTyping = requiresComplexTyping(ids, translations, idx);

        if (withComplexTyping) return generateEmptyFunction(key);

        return `${trimQuotes(key)}: ""${
          rawValue.trim().endsWith(",") ? "," : ""
        }`;
      }
      return `${trimQuotes(key)}: "${rawValue}`;
    })
    .join("\n");

export const getLocale = (locale, json, ids, translations) => {
  const pre = `
  import { template } from "../templating";

  export type Translations${locale.toUpperCase()} = typeof ${locale};

  const ${locale} = `;

  const post = ` as const;

  export default ${locale};
    `;

  const normalizedData = JSON.stringify(JSON.parse(json), null, 2);
  const normalizedIds = JSON.stringify(JSON.parse(ids), null, 2);
  const processedData = processJsonToLocale(
    normalizedData,
    normalizedIds,
    translations,
  );

  return `${pre}${processedData}${post}`;
};
